import type { Board, GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../jsxgraph-theme.service';
import {
  createDefaultAxes,
  DEFAULT_AXIS_LABEL_OFFSET_HORIZONTAL,
  DEFAULT_AXIS_LABEL_OFFSET_VERTICAL,
  DEFAULT_BOUNDING_BOX,
  DEFAULT_GRID_CONFIG,
  DEFAULT_TICK_LABEL_OFFSET_SECONDARY_HORIZONTAL,
  DEFAULT_TICK_LABEL_OFFSET_X_AXIS,
  DEFAULT_TICK_LABEL_OFFSET_Y_AXIS,
} from './coordinate-system.defaults';
import {
  boundingBoxToTuple,
  type AxisConfig,
  type AxisLabelConfig,
  type AxisOrientation,
  type BoundingBoxTuple,
  type CoordinateSystemConfig,
} from './coordinate-system.model';

export interface CoordinateSystemCreatorOptions {
  board: Board;
  themeService: JsxgraphThemeService;
  config?: CoordinateSystemConfig;
}

export interface CoordinateSystemResult {
  objects: Map<string, GeometryElement>;
}

// =============================================================================
// LABEL FORMATTERS
// =============================================================================

function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : Math.abs(a);
}

/**
 * Format a value as a multiple of π.
 * E.g., π/2 → "π/2", π → "π", 3π/2 → "3π/2", 2π → "2π"
 */
function formatPiLabel(value: number, step: number): string {
  const den = Math.round(Math.PI / step);
  const k = Math.round(value / step);

  if (Math.abs(value - k * step) > 1e-8) {
    return '';
  }
  if (k === 0) {
    return '0';
  }

  const g = gcd(k, den);
  const n = k / g;
  const d = den / g;
  const sign = n < 0 ? '−' : '';
  const an = Math.abs(n);

  if (d === 1) {
    return sign + (an === 1 ? 'π' : `${an}π`);
  }
  return sign + (an === 1 ? `π/${d}` : `${an}π/${d}`);
}

/**
 * Format a value (in radians) as degrees.
 * E.g., π/2 → "90°", π → "180°"
 */
function formatDegreesLabel(value: number, step: number): string {
  const k = Math.round(value / step);
  if (Math.abs(value - k * step) > 1e-8) {
    return '';
  }
  return `${Math.round((value * 180) / Math.PI)}°`;
}

// =============================================================================
// HELPERS
// =============================================================================

function createAxisLabel(
  board: Board,
  orientation: AxisOrientation,
  config: AxisLabelConfig,
  boundingbox: BoundingBoxTuple,
  theme: ReturnType<JsxgraphThemeService['getTheme']>,
  axisPosition: number,
): GeometryElement {
  const [, top, right] = boundingbox;
  const [x, y] = orientation === 'horizontal' ? [right, axisPosition] : [axisPosition, top];
  const defaultOffset =
    orientation === 'horizontal' ? DEFAULT_AXIS_LABEL_OFFSET_HORIZONTAL : DEFAULT_AXIS_LABEL_OFFSET_VERTICAL;

  return board.create('text', [x, y, config.text], {
    anchorX: orientation === 'horizontal' ? 'right' : 'left',
    anchorY: orientation === 'horizontal' ? 'bottom' : 'top',
    offset: config.offset ?? defaultOffset,
    fontSize: config.fontSize ?? theme.typography.fontSize.base,
    cssStyle: `font-family: ${theme.typography.fontFamily}; font-style: italic;`,
    fixed: true,
    highlight: false,
  }) as GeometryElement;
}

// =============================================================================
// MAIN CREATOR
// =============================================================================

export function createCoordinateSystem(opts: CoordinateSystemCreatorOptions): CoordinateSystemResult {
  const { board, themeService, config } = opts;
  const objects = new Map<string, GeometryElement>();

  if (!board) {
    return { objects };
  }

  const theme = themeService.getTheme();

  // Grid config
  const gridConfig = config?.grid ?? DEFAULT_GRID_CONFIG;
  const gridEnabled = gridConfig.visible !== false;
  const gridType = gridConfig.type ?? 'lines';

  const bbox: BoundingBoxTuple = config?.boundingbox
    ? boundingBoxToTuple(config.boundingbox)
    : (board.getBoundingBox() as BoundingBoxTuple | null) ?? boundingBoxToTuple(DEFAULT_BOUNDING_BOX);

  // Global ticks config - merges into axis ticks as defaults
  const globalTicks = config?.ticks;

  // Axes config - use default if not specified, { visible: false } disables axes
  const axesConfig: AxisConfig[] = config?.axes?.visible === false ? [] : config?.axes?.custom ?? createDefaultAxes();

  // Determine primary axes (first of each orientation, or explicitly marked)
  const processedAxes = axesConfig.map((axis) => {
    const sameOrientationAxes = axesConfig.filter((a) => a.orientation === axis.orientation);
    const isFirstOfOrientation = sameOrientationAxes[0] === axis;
    const isPrimary = axis.isPrimary ?? isFirstOfOrientation;

    return { ...axis, isPrimary };
  });

  // Create axes
  const baseAxisOptions = {
    strokeColor: theme.colors.axis,
    strokeWidth: 1,
    highlight: false,
    straightFirst: true,
    straightLast: true,
  };

  for (const axisConfig of processedAxes) {
    const { id, orientation, position = 0, isPrimary, axisLabel } = axisConfig;

    // Merge global ticks into per-axis ticks (per-axis takes precedence)
    const ticksConfig = axisConfig.ticks ? { ...globalTicks, ...axisConfig.ticks } : globalTicks;
    const ticksVisible = ticksConfig?.visible !== false;

    const isVertical = orientation === 'vertical';

    // Determine axis points based on orientation and position
    const points: [[number, number], [number, number]] =
      orientation === 'horizontal'
        ? [
            [0, position],
            [1, position],
          ]
        : [
            [position, 0],
            [position, 1],
          ];

    // Build generateLabelText function if needed for π or degrees formatting
    let generateLabelText: ((tick: unknown, zero: unknown, value?: number | string | null) => string) | undefined;
    if (ticksConfig?.labelFormat === 'pi' || ticksConfig?.labelFormat === 'degrees') {
      const step = ticksConfig.ticksDistance ?? Math.PI / 2;
      // For horizontal axis use x-coordinate (index 1), for vertical use y-coordinate (index 2)
      const coordIndex = isVertical ? 2 : 1;
      generateLabelText = (tick, _zero, value) => {
        let tickVal: number;
        if (typeof value === 'number' && !isNaN(value)) {
          tickVal = value;
        } else {
          // Extract coordinate from tick object when value is not provided
          const t = tick as { usrCoords?: number[] };
          tickVal = t?.usrCoords ? t.usrCoords[coordIndex] : 0;
        }
        return ticksConfig.labelFormat === 'pi' ? formatPiLabel(tickVal, step) : formatDegreesLabel(tickVal, step);
      };
    }

    // Determine tick label offset based on axis orientation and primary status
    const defaultLabelOffset = isVertical
      ? DEFAULT_TICK_LABEL_OFFSET_Y_AXIS
      : !isPrimary
        ? DEFAULT_TICK_LABEL_OFFSET_SECONDARY_HORIZONTAL
        : DEFAULT_TICK_LABEL_OFFSET_X_AXIS;

    // Build ticks options - all configuration goes directly to JSXGraph's native ticks
    const ticksOptions: Record<string, unknown> = {
      strokeColor: theme.colors.axis,
      strokeWidth: 1,
      majorHeight: !ticksVisible ? 0 : ticksConfig?.majorHeight ?? 5,
      minorHeight: !ticksVisible ? 0 : ticksConfig?.minorHeight ?? 0,
      minorTicks: ticksConfig?.minorTicks ?? 0,
      drawLabels: !ticksVisible ? false : ticksConfig?.drawLabels ?? true,
      drawZero: ticksConfig?.drawZero ?? false,
      ticksDistance: ticksConfig?.ticksDistance,
      insertTicks: ticksConfig?.ticksDistance === undefined,
      strokeOpacity: isPrimary ? 1 : 0,
      label: {
        fontSize: ticksConfig?.label?.fontSize ?? theme.typography.fontSize.small,
        cssStyle: `font-family: ${theme.typography.fontFamily}; color: ${theme.colors.textSecondary};`,
        offset: ticksConfig?.label?.offset ?? defaultLabelOffset,
        anchorX: isVertical ? 'left' : 'middle',
        anchorY: isVertical ? 'middle' : 'top',
      },
    };

    // Add optional JSXGraph tick options if defined
    if (ticksConfig?.scale !== undefined) {
      ticksOptions['scale'] = ticksConfig.scale;
    }
    if (ticksConfig?.scaleSymbol !== undefined) {
      ticksOptions['scaleSymbol'] = ticksConfig.scaleSymbol;
    }
    if (ticksConfig?.fixedTicks !== undefined) {
      ticksOptions['fixedTicks'] = ticksConfig.fixedTicks;
    }
    if (ticksConfig?.labels !== undefined) {
      ticksOptions['labels'] = ticksConfig.labels;
    }
    if (generateLabelText) {
      ticksOptions['generateLabelText'] = generateLabelText;
    }

    const axis = board.create('axis', points, {
      ...baseAxisOptions,
      name: id,
      withLabel: false,
      ticks: ticksOptions,
    }) as GeometryElement;

    objects.set(id, axis);

    // Create axis label if configured
    if (axisLabel) {
      objects.set(`${id}-label`, createAxisLabel(board, orientation, axisLabel, bbox, theme, position));
    }
  }

  // Create grid after axes (native grid can reference parent axis elements)
  if (gridEnabled) {
    const isDots = gridType === 'dots';
    const gridFace = isDots ? 'point' : 'line';

    const majorStep = 'auto';

    const parentAxes = [
      objects.get(processedAxes.find((a) => a.orientation === 'horizontal' && a.isPrimary)?.id ?? ''),
      objects.get(processedAxes.find((a) => a.orientation === 'vertical' && a.isPrimary)?.id ?? ''),
    ].filter(Boolean);

    const grid = board.create('grid', parentAxes, {
      majorStep,
      fillColor: 'none',
      major: {
        face: gridFace,
        drawZero: false,
        size: isDots ? 6 : undefined,
        strokeColor: theme.colors.grid,
        fillColor: 'none',
        strokeOpacity: 1,
        strokeWidth: 1,
        // JSXGraph curves default lineCap to 'round', which creates visible dots
        // at every NaN-separated segment endpoint in line grids. Override to 'butt'.
        lineCap: 'butt',
      },
      minor: {
        visible: false,
      },
      minorElements: 0,
      highlight: false,
      layer: 0,
      visible: true,
    }) as GeometryElement;

    // Workaround: JSXGraph's setObjectFillColor() does not set fill="none" on the
    // SVG node — it only sets fill-opacity:0, leaving the default black fill. This
    // causes artifacts with certain renderers. Patch the rendNode directly.
    const gridAny = grid as unknown as Record<string, unknown>;
    const majorNode = grid.rendNode as unknown as SVGElement | undefined;
    if (majorNode) {
      majorNode.setAttribute('fill', 'none');
    }
    const minorGrid = gridAny['minorGrid'] as { rendNode?: SVGElement } | undefined;
    if (minorGrid?.rendNode) {
      minorGrid.rendNode.setAttribute('fill', 'none');
    }

    objects.set('grid', grid);
  }

  return { objects };
}
